import { createContext } from 'react';
import {
  makeApiProvider,
  useApiQuery,
} from '@pflegenavi/shared-frontend/platform';
import type {
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { useMutation, useQueryClient } from 'react-query';
import type { ServiceProviderPaymentsStripeAccountControllerResultTypes } from '@pflegenavi/service-provider-shared/api-endpoints';
import type { IServiceProviderStripeApi } from './api';
import { ServiceProviderStripeApi } from './api';

const ApiContext = createContext<IServiceProviderStripeApi | undefined>(
  undefined
);
const {
  useApi: useServiceProviderStripeApi,
  ApiProvider: ServiceProviderStripeApiProvider,
} = makeApiProvider({
  name: 'ServiceProviderStripe',
  ApiContext,
  newApi: (tenantId, auth, apiUrl) =>
    new ServiceProviderStripeApi(tenantId, auth, apiUrl),
});

export {
  useServiceProviderStripeApi,
  ServiceProviderStripeApiProvider,
  ApiContext as ServiceProviderStripeApiContext,
};
export type { IServiceProviderStripeApi } from './api';

const SERVICE_PROVIDER_STRIPE_ACCOUNT_KEY = (
  globalServiceProviderId: string | undefined
) => [`service-provider-stripe-account`, globalServiceProviderId];

export const useServiceProviderStripeAccount = (
  params: {
    globalServiceProviderId: string | undefined;
  },
  options?: UseQueryOptions<
    ServiceProviderPaymentsStripeAccountControllerResultTypes['getServiceProviderStripeAccount']
  >
): UseQueryResult<
  ServiceProviderPaymentsStripeAccountControllerResultTypes['getServiceProviderStripeAccount']
> => {
  return useApiQuery(
    // eslint-disable-next-line @grncdr/react-hooks/rules-of-hooks
    () => useServiceProviderStripeApi(),
    SERVICE_PROVIDER_STRIPE_ACCOUNT_KEY(params.globalServiceProviderId),
    (api) => {
      if (params.globalServiceProviderId === undefined) {
        throw new Error('globalServiceProviderId is undefined');
      }
      return api?.getServiceProviderStripeAccount({
        params: {
          globalServiceProviderId: params.globalServiceProviderId,
        },
      }) as Promise<
        ServiceProviderPaymentsStripeAccountControllerResultTypes['getServiceProviderStripeAccount']
      >;
    },
    {
      ...options,
      enabled: !!params.globalServiceProviderId,
    }
  );
};

export const useCreateServiceProviderStripeAccount = (): UseMutationResult<
  ServiceProviderPaymentsStripeAccountControllerResultTypes['createServiceProviderStripeAccount'],
  unknown,
  {
    globalServiceProviderId: string;
  }
> => {
  const api = useServiceProviderStripeApi();
  const queryClient = useQueryClient();

  return useMutation<
    ServiceProviderPaymentsStripeAccountControllerResultTypes['createServiceProviderStripeAccount'],
    unknown,
    {
      globalServiceProviderId: string;
    }
  >(
    'update_service_provider_stripe_account',
    (data) =>
      api?.createServiceProviderStripeAccount({
        params: { globalServiceProviderId: data.globalServiceProviderId },
        body: undefined,
      }) as Promise<
        ServiceProviderPaymentsStripeAccountControllerResultTypes['createServiceProviderStripeAccount']
      >,
    {
      onSuccess: async (data, props) => {
        return await queryClient.invalidateQueries(
          SERVICE_PROVIDER_STRIPE_ACCOUNT_KEY(props.globalServiceProviderId)
        );
      },
    }
  );
};

export const useRefetchServiceProviderStripeAccount = (): UseMutationResult<
  ServiceProviderPaymentsStripeAccountControllerResultTypes['refetchServiceProviderStripeAccount'],
  unknown,
  {
    globalServiceProviderId: string;
  }
> => {
  const api = useServiceProviderStripeApi();
  const queryClient = useQueryClient();

  return useMutation<
    ServiceProviderPaymentsStripeAccountControllerResultTypes['refetchServiceProviderStripeAccount'],
    unknown,
    {
      globalServiceProviderId: string;
    }
  >(
    'refetch_service_provider_stripe_account',
    (data) =>
      api?.refetchServiceProviderStripeAccount({
        params: { globalServiceProviderId: data.globalServiceProviderId },
        body: undefined,
      }) as Promise<
        ServiceProviderPaymentsStripeAccountControllerResultTypes['refetchServiceProviderStripeAccount']
      >,
    {
      onSuccess: async (data, props) => {
        return await queryClient.invalidateQueries(
          SERVICE_PROVIDER_STRIPE_ACCOUNT_KEY(props.globalServiceProviderId)
        );
      },
    }
  );
};
